import {ResponseErrorV2} from '../CommonInterfaces';
import InternalAPIServiceV2 from './InternalAPIServiceV2';

export interface Item {
  companyId: number;
  id: number;
  sku: string;
  description: string;
  inventoryGroup?: string;
  cartonsPerLayer?: number;
  layersPerPallet?: number;
  cartonsPerPallet?: number;
  stackable?: boolean;
  lotCodeTrackingEnabled?: boolean;
  expiryDateTrackingEnabled?: boolean;
  bagAndTapeRequired?: boolean;
  isHazmat: boolean;
  isOrmd: boolean;
  unNumber?: string;
  pkgCode?: string;
  releaseDate?: string;
  velocity?: string;
  properties: ItemResponseProperties[];
}

export interface ItemResponseProperties {
  parentPackaging: string;
  packaging: string;
  unitsPerParent?: number;
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: string;
  weight?: number;
  weightUnit?: string;
  shipAlone?: boolean;
  shipAsIs?: boolean;
  barcode?: string;
  barcodeType?: string;
  serialNumberCaptureMode?: string;
  serialNumberPatterns?: string;
}

export interface ItemsResponse {
  data: {items: Item[]; continuationToken: string};
  errors: ResponseErrorV2[];
}

export interface BulkItemUpsertRequest {
  shipperCompanyId: number;
  itemsToUpsert: ItemToUpsert[];
}

export interface ItemToUpsert {
  sku: string;
  description?: string;
  alternateId?: string;
  internationalShippingInfo?: InternationalShippingInfo;
  isHazmat?: boolean;
  limitedQuantity?: boolean;
  unNumber?: string;
  pkgCode?: string;
  lotCodeTrackingEnabled?: boolean;
  expiryDateTrackingEnabled?: boolean;
  releaseDate?: string;
  inventoryGroup?: string;
  csa?: Map<string, string>;
  velocity?: Velocity;
  serialNumberProgram?: string;
  defaultBuildInstructions?: DefaultBuildInstructions;
  stackable?: boolean;
  bagAndTapeRequired?: boolean;
  reviewDate?: string;
  properties?: ItemPropertyToUpsert[];
  shelfLifeThreshold?: number;
  newSku?: number;
}

export interface ItemPropertyToUpsert {
  packaging: Packaging;
  parentPackaging?: Packaging;
  barcodeType?: string;
  barcode?: string;
  shipAsIs?: boolean;
  shipAlone?: boolean;
  unitsPerParent?: number;
  dimensionUnit?: DimensionUnit;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  weightUnit?: WeightUnit;
  retailAmount?: number;
  serialNumberCaptureMode?: SerialNumberCaptureMode;
  serialNumberPatterns?: string[];
}

export type SerialNumberCaptureMode = 'NONE' | 'INBOUND_CAPTURE' | 'OUTBOUND_CAPTURE';

export type WeightUnit = 'pound' | 'lb' | 'oz' | 'g' | 'kg';

export type DimensionUnit = 'inch' | 'in' | 'm' | 'cm';

export type ParentPackaging = 'inner_pack' | 'carton' | 'pallet';

export type Packaging = 'each' | ParentPackaging;

export interface DefaultBuildInstructions {
  cartonsPerLayer: number;
  layersPerPallet: number;
  cartonsPerPallet: number;
}

export type Velocity = 'slow' | 'steady' | 'fast';

export interface InternationalShippingInfo {
  hsCode?: string;
  countryOfOrigin?: string;
}

export interface BulkItemUpsertResponse {
  isSuccess: boolean;
  errors?: BulkItemUpsertError[];
}

export interface BulkItemUpsertError {
  code: string;
  detail: string;
  sourceParam: string;
  sourcePointer: string;
}

class ItemMasterService extends InternalAPIServiceV2 {
  constructor() {
    super('/v2/item-master');
  }

  public async bulkUpsertItemMaster(request: BulkItemUpsertRequest): Promise<BulkItemUpsertResponse> {
    const res = await this.makePostRequest(`/v2/items/bulk-upsert`, request);
    if (!res) {
      return {isSuccess: true};
    }
    if (res.errors && res.errors.length > 0) {
      return {
        isSuccess: false,
        errors: res.errors.map((e) => {
          return {
            code: e.code || '',
            detail: e.detail || 'Unknown failure encountered.',
            sourceParam: e.source.param || '',
            sourcePointer: e.source.pointer || ''
          };
        })
      };
    }
  }

  public getItems(itemIds: number[], continuationToken: string): Promise<ItemsResponse | null> {
    // InternalAPIServiceV2 doesn't follow our array query parameter guidelines
    // from https://flexe-inc.atlassian.net/wiki/spaces/ENG/pages/2400944181/Flexe+API+Standards.
    // Instead, build them ourselves with URLSearchParams.
    const params = new URLSearchParams();
    for (const id of itemIds) {
      params.append('item-ids[]', id.toString());
    }
    if (continuationToken) {
      params.append('continuation-token', continuationToken);
    }

    // We need {} to make axios respect Content-Type header. Ref: https://github.com/axios/axios/issues/89.
    // It's possible this workaround can be removed if https://flexe-inc.atlassian.net/browse/IMS-7100 is fixed.
    return this.makeGetRequest(`${this.baseUrl}`, params, {}, {'Content-Type': 'application/json'});
  }
}

export default ItemMasterService;
